import type { Driver, DriveStep } from 'driver.js'
import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { driver } from 'driver.js'
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { orderKeys } from '@/entities/order/model'
import { stateKeys } from '@/entities/state/model'
import { storeKeys } from '@/entities/store/api'
import { OrderDetailsModal } from '@/features/order/ui/order-details/modal'
import { cleanupDriverElements, destroyDriverInstances, disableTutorialMode, enableTutorialMode, injectTutorialHeaderStyle, setCurrentTutorialStep } from '../../lib'
import { CompletionModal } from './CompletionModal'
import { TutorialHeader } from './TutorialHeader'
import { TutorialLoadingModal } from './TutorialLoadingModal'

export interface DriverJsContextType {
  startTutorial: () => Promise<void>
  endTutorial: () => void
  isActive: boolean
  isStartingTutorial: boolean
  showTutorialModal: boolean
  tutorialOrderId: string | null
}

const DriverJsContext = createContext<DriverJsContextType | null>(null)

// Export context for use in hook
export { DriverJsContext }

interface DriverJsProviderProps {
  children: React.ReactNode
}

const TUTORIAL_HEADER_HEIGHT = 56
const TUTORIAL_HEADER_BUFFER = 8

injectTutorialHeaderStyle(TUTORIAL_HEADER_HEIGHT)

export function DriverJsProvider({ children }: DriverJsProviderProps) {
  const [isActive, setIsActive] = useState(false)
  const [isStartingTutorial, setIsStartingTutorial] = useState(false)
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [showTutorialModal, setShowTutorialModal] = useState(false)
  const [tutorialOrderId, setTutorialOrderId] = useState<string | null>(null)
  const driverRef = useRef<Driver | null>(null)
  const activeDriverRef = useRef<Driver | null>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  useEffect(() => {
    // Load CSS only in browser (not Node.js/Playwright)
    if (typeof window !== 'undefined') {
      import('./tutorial-styles.css').catch((error) => {
        console.error('Failed to load tutorial-styles.css:', error)
      })
      import('./TutorialHeader.css').catch((error) => {
        console.error('Failed to load TutorialHeader.css:', error)
      })
      import('./TutorialModal.css').catch((error) => {
        console.error('Failed to load TutorialModal.css:', error)
      })
    }
  }, [])

  const isOnboardingDisabled = import.meta.env.VITE_DISABLE_ONBOARDING === 'true'

  const resetTutorialState = useCallback(async () => {
    setIsActive(false)
    setIsStartingTutorial(false)
    setCurrentTutorialStep(0)
    setShowTutorialModal(false)
    setTutorialOrderId(null)
  }, [])

  const handleTutorialExit = useCallback(async () => {
    try {
      const success = await disableTutorialMode()

      queryClient.invalidateQueries({ queryKey: orderKeys.orders() })
      queryClient.invalidateQueries({ queryKey: orderKeys.waitTime() })
      queryClient.invalidateQueries({ queryKey: stateKeys.stateConfig() })
      queryClient.invalidateQueries({ queryKey: storeKeys.details() })

      queryClient.refetchQueries({ queryKey: orderKeys.orders() })
      queryClient.refetchQueries({ queryKey: orderKeys.waitTime() })
      queryClient.refetchQueries({ queryKey: stateKeys.stateConfig() })
      queryClient.refetchQueries({ queryKey: storeKeys.details() })

      if (success) {
        navigate({
          to: '/dashboard',
          search: '',
          replace: true,
          resetScroll: true,
        })
      }
    } catch (error) {
      console.error('Error disabling tutorial mode:', error)
      navigate({
        to: '/dashboard',
        search: '',
        replace: true,
        resetScroll: true,
      })
    }
  // eslint-disable-next-line react/exhaustive-deps
  }, [navigate])

  // Simple cleanup function to avoid 'used before defined' issues
  const cleanupAndStop = useCallback(async () => {
    destroyDriverInstances(driverRef, activeDriverRef)
    resetTutorialState()
    cleanupDriverElements()
    await handleTutorialExit()
  }, [handleTutorialExit, resetTutorialState])

  const getTutorialSteps = useCallback((): DriveStep[] => {
    const isMobile = window.innerWidth <= 768

    return [
      {
        element: '[data-fake="true"]', // Purposely use a fake element to ensure the tutorial modal starts tutorial + uses overlay and postions in centre.
        popover: {
          title: 'How to guide',
          description: '<h3>Hello Dashboard</h3><p>This onboarding tutorial will guide you through the Hello Dashboard. You\'ll learn how to successfully prepare, acknowledge and deliver an order.</p>',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-onboarding="header-actions"]',
        popover: {
          title: 'Step 1 of 7',
          description: '<h3>Main menu, Sound, Search</h3><p>The main menu gives you access to Active Orders and Order History. The speaker icon mutes/unmutes notification chimes. Use the search button to find orders.</p>',
          side: 'right',
          align: 'center',
        },
      },
      {
        element: '[data-onboarding="wait-times-widget"]',
        popover: {
          title: 'Step 2 of 7',
          description: '<h3>Wait Times</h3><p>Average wait time for all customer orders are displayed here. Our target wait time is 5-minutes.</p>',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-onboarding="section-on-the-way"] [data-onboarding="order-card"]',
        popover: {
          title: 'Step 3 of 7',
          description: '<h3>Prepare Order</h3><p>When an order moves to "On the way," start preparing it for the customer\'s arrival. The 🥶 emoji is a reminder for frozen crates, while 🆔 indicates restricted items requiring a customer ID check. To view order details and crate information, simply click on the card.</p>',
          side: isMobile ? 'top' : 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-onboarding="section-on-the-way"] [data-onboarding="group-order-card"]',
        popover: {
          title: 'Step 4 of 7',
          description: '<h3>Partner Delivery</h3><p>Grouped Partner orders will have a blue ring for easy identification. Click on the order ticket to see crates to be consolidated. If a driver is collecting multiple orders, request that they be separated.</p>',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-onboarding="section-arrived"] [data-onboarding="order-card"]',
        popover: {
          title: 'Step 5 of 7',
          description: '<h3>Acknowledging Orders</h3><p>When an order moves to \'Arrived,\' it appears with a green border. Tap \'We\'re on it\' to acknowledge it, which will notify the customer that their order is being prepared, keeping them informed.</p>',
          side: isMobile ? 'top' : 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-onboarding="section-delivering"] [data-onboarding="order-card"]',
        popover: {
          title: 'Step 6 of 7',
          description: '<h3>Deliver and Complete</h3><p>When you transition an order to \'Delivering,\' it appears at the top, with the most urgent order first. Upon reaching the customers, tap \'Complete\' to update the status and finalise the tracked time.</p>',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-onboarding="tutorial-modal-placeholder"]',
        popover: {
          title: 'Step 7 of 7',
          description: '<h3>Order Details</h3><p>To view order details, simply click on any card and it will bring you to this screen. Here you can view crate information, van/sequence numbers, manually update the order status and view information.</p>',
          side: 'top',
          align: 'center',
        },
      },
    ]
  }, [])

  const startTutorial = useCallback(async () => {
    // Short-circuit if onboarding is disabled
    if (isOnboardingDisabled) {
      return
    }

    try {
      if (driverRef.current) {
        driverRef.current.destroy()
      }

      setIsStartingTutorial(true)

      const success = await enableTutorialMode()

      if (success) {
        queryClient.invalidateQueries()
        // Set React Query mock data from window.__tutorialData
        if (window.__tutorialData) {
          queryClient.setQueryData(orderKeys.orders(), window.__tutorialData.orders || [])
          queryClient.setQueryData(orderKeys.waitTime(), window.__tutorialData.waitTime || {})
          queryClient.setQueryData(stateKeys.stateConfig(), window.__tutorialData.stateConfig || {})
          queryClient.setQueryData(storeKeys.details(), window.__tutorialData.storeDetails || {})
        }

        await waitForOrderCards({ maxAttempts: 50, interval: 100 })
      }

      async function waitForOrderCards({ maxAttempts = 50, interval = 100 } = {}) {
        for (let attempts = 0; attempts < maxAttempts; attempts++) {
          const headerActions = document.querySelector('[data-onboarding="header-actions"]')
          const orderCards = document.querySelectorAll('[data-onboarding="order-card"]')
          if (headerActions && orderCards.length >= 3) {
            return
          }
          await new Promise(resolve => setTimeout(resolve, interval))
        }
      }
    } catch (error) {
      console.error('Error enabling tutorial mode:', error)
      setIsStartingTutorial(false)
    }

    setCurrentTutorialStep(0)
    setIsActive(true)
    await waitForTutorialHeader()

    async function waitForTutorialHeader({ maxAttempts = 30, interval = 50, settleDelay = 200 } = {}) {
      for (let attempts = 0; attempts < maxAttempts; attempts++) {
        const tutorialHeader = document.querySelector('[data-tutorial-header="true"]')
        const bodyHasClass = document.body.classList.contains('tutorial-header-active')
        if (tutorialHeader && bodyHasClass) {
          await new Promise(resolve => requestAnimationFrame(resolve))
          await new Promise(resolve => setTimeout(resolve, settleDelay))
          return
        }
        await new Promise(resolve => setTimeout(resolve, interval))
      }
    }

    const driverInstance = driver({
      showProgress: false,
      showButtons: ['next', 'previous', 'close'],
      steps: getTutorialSteps(),
      smoothScroll: true,
      stagePadding: 2,
      animate: false,
      allowClose: true,
      onNextClick: (_element, _step, { state }) => {
        const currentStep = (state?.activeIndex || 0) + 1

        if (currentStep === 7) {
          const isMobile = window.innerWidth <= 768
          setTutorialOrderId('100001')
          setShowTutorialModal(true)

          const waitForModalAndHighlight = () => {
            setTimeout(() => {
              let attempts = 0
              const maxAttempts = 20

              const findAndHighlight = () => {
                attempts++
                const targetElement = isMobile
                  ? document.querySelector('[data-onboarding="update-status-button"]')
                  : document.querySelector('.mantine-Modal-content')

                if (targetElement) {
                  if (driverRef.current) {
                    driverRef.current.destroy()
                  }

                  const modalDriver = driver({
                    showProgress: false,
                    showButtons: ['next', 'previous'],
                    stagePadding: 0,
                    animate: false,
                    allowClose: false,
                    steps: [
                      {
                        element: targetElement,
                        popover: {
                          title: 'Step 7 of 7',
                          description: '<h3>Order Details</h3><p>To view order details, simply click on any card and it will bring you to this screen. Here you can view crate information, van/sequence numbers, manually update the order status and view information.</p>',
                          side: 'top',
                          align: 'center',
                        },
                      },
                    ],
                    onNextClick: () => {
                      setShowTutorialModal(false)
                      setTutorialOrderId(null)
                      modalDriver.destroy()

                      // Ensure completion modal shows after cleanup
                      setTimeout(() => {
                        setCurrentTutorialStep(0)
                        setShowCompletionModal(true)
                      }, 100)
                    },
                    onPopoverRender: () => {
                      const nextButton = document.querySelector('.driver-popover-next-btn') as HTMLButtonElement
                      const prevButton = document.querySelector('.driver-popover-prev-btn') as HTMLButtonElement

                      if (nextButton) {
                        nextButton.innerHTML = 'Next →'
                        if (prevButton) {
                          prevButton.style.display = 'none'
                        }
                      }
                    },
                    onCloseClick: () => cleanupAndStop(),
                  })

                  driverRef.current = modalDriver
                  activeDriverRef.current = modalDriver
                  modalDriver.drive()
                  return
                }

                if (attempts < maxAttempts) {
                  setTimeout(findAndHighlight, 100)
                }
              }

              findAndHighlight()
            }, 100)
          }

          waitForModalAndHighlight()
          return
        }

        if (currentStep === 8) {
          setShowTutorialModal(false)
          setTutorialOrderId(null)

          if (driverRef.current) {
            driverRef.current.destroy()
            driverRef.current = null
          }

          if (activeDriverRef.current && activeDriverRef.current !== driverRef.current) {
            activeDriverRef.current.destroy()
            activeDriverRef.current = null
          }

          // Ensure completion modal shows after cleanup
          setTimeout(() => {
            setCurrentTutorialStep(0)
            setShowCompletionModal(true)
          }, 100)
          return
        }

        driverRef.current?.moveNext()
      },
      onPopoverRender: ({ wrapper }, { state }) => {
        const nextButton = document.querySelector('.driver-popover-next-btn') as HTMLButtonElement
        const prevButton = document.querySelector('.driver-popover-prev-btn') as HTMLButtonElement

        if (nextButton) {
          const currentStep = (state?.activeIndex || 0) + 1

          if (currentStep === 8) {
            nextButton.innerHTML = 'Next →'
          } else {
            nextButton.innerHTML = 'Next →'
          }

          if (prevButton) {
            prevButton.style.display = 'none'
          }
        }

        if (wrapper) {
          wrapper.style.zIndex = '99999'
          wrapper.style.transition = 'opacity 0.3s ease, transform 0.3s ease'
          wrapper.style.opacity = '0'
          wrapper.style.transform = 'translateY(-10px)'

          // Force reflow to ensure initial styles are applied
          void wrapper.offsetHeight

          requestAnimationFrame(() => {
            const minTopPosition = TUTORIAL_HEADER_HEIGHT + TUTORIAL_HEADER_BUFFER
            const viewportHeight = window.innerHeight

            wrapper.style.display = 'block'
            const popoverRect = wrapper.getBoundingClientRect()

            let finalTopPosition = Number.parseInt(wrapper.style.top || '0')

            // Check if popover would be covered by tutorial header
            if (popoverRect.top < minTopPosition && popoverRect.top > -100) {
              // If there's enough space below the target element, reposition to bottom
              const targetElement = document.querySelector('.driver-active-element')
              if (targetElement) {
                const targetRect = targetElement.getBoundingClientRect()
                const spaceBelow = viewportHeight - (targetRect.bottom + 20)
                const spaceAbove = targetRect.top - minTopPosition

                if (spaceBelow > popoverRect.height && spaceBelow > spaceAbove) {
                  // Reposition to bottom of target element
                  finalTopPosition = targetRect.bottom + 10
                } else {
                  // Fallback to adjusting position below header
                  const adjustment = minTopPosition - popoverRect.top
                  finalTopPosition += adjustment
                }
              } else {
                // Fallback to standard adjustment
                const adjustment = minTopPosition - popoverRect.top
                finalTopPosition += adjustment
              }
            }

            const tempRect = { ...popoverRect, top: finalTopPosition }
            if (tempRect.top + popoverRect.height > viewportHeight - 20) {
              const overflowAdjustment = (tempRect.top + popoverRect.height) - (viewportHeight - 20)
              finalTopPosition -= overflowAdjustment
            }

            if (finalTopPosition < 0) {
              finalTopPosition = minTopPosition
            }

            wrapper.style.top = `${finalTopPosition}px`

            setTimeout(() => {
              wrapper.style.opacity = '1'
              wrapper.style.transform = 'translateY(0)'
            }, 50)
          })
        }
      },
      onHighlightStarted: (_element, _step, options) => {
        const stepNumber = (options.state?.activeIndex || 0) + 1
        setCurrentTutorialStep(stepNumber)

        const targetElement = _element
        if (targetElement) {
          const rect = targetElement.getBoundingClientRect()

          if (rect.width === 0 || rect.height === 0) {
            // Element has zero dimensions - may need visibility check
          }

          if (rect.top < 0 || rect.top > window.innerHeight) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }

        if (stepNumber !== 8 && showTutorialModal) {
          setShowTutorialModal(false)
          setTutorialOrderId(null)
        }
      },
      onDestroyStarted: () => {
        cleanupAndStop()
      },
      onCloseClick: () => {
        cleanupAndStop()
      },
    })

    driverRef.current = driverInstance
    activeDriverRef.current = driverInstance

    window.driverInstance = driverInstance

    setIsActive(true)
    setIsStartingTutorial(false)
    setCurrentTutorialStep(1)
    driverInstance.drive()
  }, [queryClient, getTutorialSteps, cleanupAndStop, showTutorialModal, isOnboardingDisabled])

  const endTutorial = useCallback(async () => {
    // Short-circuit if onboarding is disabled
    if (isOnboardingDisabled) {
      return
    }

    destroyDriverInstances(driverRef, activeDriverRef)
    resetTutorialState()
    cleanupDriverElements()
    await handleTutorialExit()
  }, [handleTutorialExit, resetTutorialState, isOnboardingDisabled])

  const handleCompletionModalClose = useCallback(() => {
    setShowCompletionModal(false)
    endTutorial()
  }, [endTutorial])

  const handleRestartTutorial = useCallback(async () => {
    destroyDriverInstances(driverRef, activeDriverRef)

    const driverElements = document.querySelectorAll('.driver-popover, .driver-overlay, .driver-highlighted-element, .driver-no-interaction, .driver-fade-element, [data-driver-popover], [data-driver-overlay]')
    driverElements.forEach(element => element.remove())

    cleanupDriverElements()
    await resetTutorialState()
    await startTutorial()
    setShowCompletionModal(false)
  }, [startTutorial, resetTutorialState])

  const handlePlayAgain = useCallback(() => {
    setShowCompletionModal(false)

    if (driverRef.current) {
      driverRef.current.destroy()
      driverRef.current = null
    }

    cleanupDriverElements()

    const allElements = document.querySelectorAll('[data-onboarding]')
    allElements.forEach((element) => {
      (element as HTMLElement).style.transform = ''
      // Force reflow to ensure styles are applied
      void (element as HTMLElement).offsetHeight
    })

    resetTutorialState()

    setTimeout(() => {
      const remainingElements = document.querySelectorAll('.driver-popover, .driver-overlay, .driver-highlighted-element')
      if (remainingElements.length > 0) {
        remainingElements.forEach(el => el.remove())
      }

      handleRestartTutorial()
    }, 600)
  }, [resetTutorialState, handleRestartTutorial])

  useEffect(() => {
    // Cleanup driverjs instances on unmount
    return () => {
      destroyDriverInstances(driverRef, activeDriverRef)
    }
  }, [])

  useEffect(() => {
    // Hide all notifications when tutorial starts
    if (isStartingTutorial) {
      notifications.clean()
    }
  }, [isStartingTutorial])

  const contextValue = useMemo(() => ({
    startTutorial,
    endTutorial,
    isActive,
    isStartingTutorial,
    showTutorialModal,
    tutorialOrderId,
  }), [startTutorial, endTutorial, isActive, isStartingTutorial, showTutorialModal, tutorialOrderId])

  return (
    <DriverJsContext value={contextValue}>
      {children}
      {isActive && <TutorialHeader onClose={endTutorial} />}
      {isStartingTutorial && <TutorialLoadingModal />}
      {showTutorialModal && tutorialOrderId && (
        <OrderDetailsModal
          orderId={tutorialOrderId}
          parentPath="/dashboard?onboarding=true"
          withoutBackdrop={true}
        />
      )}
      {showCompletionModal && (
        <CompletionModal
          onClose={handleCompletionModalClose}
          onPlayAgain={handlePlayAgain}
        />
      )}
    </DriverJsContext>
  )
}
